import { MessageBar, MessageBarBody, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import cn from 'classnames';
import { findIndex, get } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../Button';
import { SearchInput } from '../../SearchInput/SearchInput';
import { TabItemChangeEvent, TabItemProps, TabsGeneric } from '../../Tabs';
import { Tooltip } from '../../Tooltip';
import './ClassbookSidebar.scss';
import { classbookSidebarItemToTreeItemProps } from './ClassbookSidebar.utils';

export enum ClassbookSidebarItemType {
  Grades = Math.trunc(1),
  Classes = 1 << 1,
  Courses = 1 << 2,
}

export interface ClassbookSidebarItem {
  id: string;
  name: string;
}

export interface SidebarTreeItem {
  key: string;
  id: string;
  title: string;
  'data-cy'?: string;
}

export interface ClassbookSidebarProps {
  /**
   * Defines the selected menu item (Grades, Classes, Courses).
   *
   * @type {ClassbookSidebarItemType}
   * @memberof ClassbookSidebarProps
   */
  selectedType: ClassbookSidebarItemType;
  /**
   * List of grades (optional).
   * If not set, a placeholder will be displayed.
   *
   * @type {ClassbookSidebarItem[]}
   * @memberof ClassbookSidebarProps
   */
  grades?: ClassbookSidebarItem[];
  /**
   * List of classes (optional).
   * If not set, a placeholder will be displayed.
   *
   * @type {ClassbookSidebarItem[]}
   * @memberof ClassbookSidebarProps
   */
  classes?: ClassbookSidebarItem[];
  /**
   * List of courses (optional).
   * If not set, a placeholder will be displayed.
   *
   * @type {ClassbookSidebarItem[]}
   * @memberof ClassbookSidebarProps
   */
  courses?: ClassbookSidebarItem[];
  isLoadingGrades?: boolean;
  isLoadingClasses?: boolean;
  isLoadingCourses?: boolean;
  onSelectType(type: ClassbookSidebarItemType): void;
  onItemClick(id: string, type: ClassbookSidebarItemType): void;
  /**
   * Bit flags describing which menu items to show (any combination of course, class, grade)
   * @type {ClassbookSidebarItemType}
   */
  menuTypeItems: ClassbookSidebarItemType;
  /**
   * If set, instead of "Stufen" or "Grades" the specified alias will be shown.
   * E.g. "Fachbereiche".
   *
   * @type {string}
   * @memberof ClassbookSidebarProps
   */
  gradesAlias?: string;
  /**
   * CSS classname
   */
  className?: string;
  /**
   * User clicked on reload data button
   */
  onReloadClick?: () => void;
}

export const ClassbookSidebar: React.FC<ClassbookSidebarProps> = ({
  selectedType,
  grades,
  classes,
  courses,
  onSelectType,
  onItemClick,
  isLoadingGrades,
  isLoadingClasses,
  isLoadingCourses,
  gradesAlias,
  menuTypeItems,
  className,
  onReloadClick,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const menuItems: TabItemProps<ClassbookSidebarItemType>[] = useMemo(() => {
    const items = [];

    if ((menuTypeItems & ClassbookSidebarItemType.Grades) === ClassbookSidebarItemType.Grades) {
      items.push({
        key: ClassbookSidebarItemType.Grades,
        'data-cy': 'classbookGradeTab',
        ariaLabel: t('classbook:grades_label', { defaultValue: 'Filtert die Liste auf Stufen' }),
        content:
          gradesAlias && gradesAlias !== ''
            ? gradesAlias
            : t('classbook:grades', { defaultValue: 'Stufen' }),
      });
    }

    if ((menuTypeItems & ClassbookSidebarItemType.Classes) === ClassbookSidebarItemType.Classes) {
      items.push({
        key: ClassbookSidebarItemType.Classes,
        ariaLabel: t('classbook:classes_label', { defaultValue: 'Filtert die Liste auf Klassen' }),
        content: t('classbook:classes', { defaultValue: 'Klassen' }),
        'data-cy': 'classbookClassTab',
      });
    }

    if ((menuTypeItems & ClassbookSidebarItemType.Courses) === ClassbookSidebarItemType.Courses) {
      items.push({
        key: ClassbookSidebarItemType.Courses,
        content: t('classbook:courses_title', { defaultValue: 'Kurse' }),
        ariaLabel: t('classbook:courses_label', { defaultValue: 'Filtert die Kurse auf Stufen' }),
        'data-cy': 'classbookCourseTab',
      });
    }

    return items;
  }, [t]);

  const handleTreeItemClick = (id: string) => {
    onItemClick(id, selectedType);
  };

  const handleTypeMenuClick = (event: TabItemChangeEvent<ClassbookSidebarItemType>) => {
    const type = get(menuItems, `${event.index}.key`, false);

    if (type === false) {
      return;
    }

    onSelectType(type);
  };

  const applyFilter = (treeItems: SidebarTreeItem[], searchQuery: string): SidebarTreeItem[] => {
    if (searchQuery.trim() === '') {
      return treeItems;
    }

    return treeItems.filter((item) => {
      if (!item.title) {
        return false;
      }

      const titleLc = item.title.toLowerCase();
      const searchQueryLc = searchQuery.toLowerCase();

      if (titleLc.includes(searchQueryLc)) {
        return true;
      }

      // Maybe user searched for "classname class" ie. "E 05a" but the
      // item title is "E (05a)""
      if (titleLc.replaceAll(/[()]/g, '').includes(searchQueryLc)) {
        return true;
      }

      // Make search for "0" prefix for classes optional
      if (titleLc.replaceAll(/[()0]/g, '').includes(searchQueryLc)) {
        return true;
      }

      return false;
    });
  };

  const getCurrentItems = (): SidebarTreeItem[] | undefined => {
    switch (selectedType) {
      case ClassbookSidebarItemType.Grades:
        return grades?.map(classbookSidebarItemToTreeItemProps);
      case ClassbookSidebarItemType.Classes:
        return classes?.map(classbookSidebarItemToTreeItemProps);
      case ClassbookSidebarItemType.Courses:
        return courses?.map(classbookSidebarItemToTreeItemProps);
    }
  };

  const itemsToShow = getCurrentItems();

  const isLoading =
    (selectedType === ClassbookSidebarItemType.Grades && isLoadingGrades) ||
    (selectedType === ClassbookSidebarItemType.Classes && isLoadingClasses) ||
    (selectedType === ClassbookSidebarItemType.Courses && isLoadingCourses);

  const tabs = TabsGeneric<ClassbookSidebarItemType>({
    items: menuItems,
    onItemClick: handleTypeMenuClick,
    className: 'tabs',
    defaultActiveIndex: findIndex(menuItems, { key: selectedType }),
  });

  return (
    <div className={cn('flex flex-col classbookSidebarWrapper', className)}>
      {tabs}
      <div className="flex items-center gap-2 searchRow">
        <SearchInput
          placeholder={t('search', { defaultValue: 'Suchen...' })}
          className="searchInput flex-1"
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value ?? '')}
          onClear={() => setSearchQuery('')}
        />
        {onReloadClick && (
          <Tooltip
            trigger={
              <Button
                appearance="transparent"
                icon={<ArrowSyncRegular />}
                onClick={onReloadClick}
                disabled={isLoading}
                aria-label={t('reload', { defaultValue: 'Neu Laden' })}
                className="flex-none"
              />
            }
            content={t('reload', { defaultValue: 'Neu Laden' })}
            relationship="label"
          />
        )}
      </div>
      {isLoading && (
        <Skeleton>
          <SkeletonItem className="listEntrySkeleton listEntrySkeletonSm" />
          <SkeletonItem className="listEntrySkeleton listEntrySkeletonMd" />
          <SkeletonItem className="listEntrySkeleton listEntrySkeletonLg" />
          <SkeletonItem className="listEntrySkeleton listEntrySkeletonSm" />
        </Skeleton>
      )}
      {!isLoading &&
        (itemsToShow && itemsToShow.length > 0 ? (
          <ul className="tree">
            {applyFilter(itemsToShow, searchQuery).map((item) => (
              <li key={item.key}>
                <button
                  type="button"
                  className="listEntry"
                  data-cy={item['data-cy']}
                  onClick={() => handleTreeItemClick(item.id)}
                >
                  <span>{item.title}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <MessageBar intent="info">
            <MessageBarBody>
              {t('list_empty', { defaultValue: 'Keine Einträge vorhanden' })}
            </MessageBarBody>
          </MessageBar>
        ))}
    </div>
  );
};
